import * as React from "react";
import { style, classes } from "typestyle";
import * as csstips from "csstips";

// This needs to be defined before render to make sure that these classes will exist
// before map is rendered. It need the container with defined size to render correctly.
const mapWrapperCN = style(
  csstips.layerParent,
  csstips.flex,
  csstips.fillParent,
  {
    position: "relative",
    zIndex: 0,
  },
);
const leafletMapControlsLayerCN = style(csstips.newLayer, {
  pointerEvents: "none",
});
const leafletMapLayerCN = style(csstips.newLayer, { zIndex: 0 });

export interface IMapContainerWithUiLayersProps {
  mapElement: React.JSX.Element;
  uiLayers: React.JSX.Element[];
}

export const MAP_ATTRIBUTION_HEIGHT_VAR = "--map-attribution-height";

// Publishes the height of Leaflet's attribution control as a CSS variable, so
// UI layers can keep their bottom controls above it (the tile credit must
// stay visible; the control grows while its sources panel is open).
const useAttributionHeightVar = (
  wrapperRef: React.RefObject<HTMLDivElement | null>,
) => {
  React.useEffect(() => {
    const wrapper = wrapperRef.current;
    const attribution = wrapper?.querySelector(".leaflet-control-attribution");
    if (!wrapper || !attribution || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => {
      wrapper.style.setProperty(
        MAP_ATTRIBUTION_HEIGHT_VAR,
        `${attribution.getBoundingClientRect().height}px`,
      );
    });
    observer.observe(attribution);
    return () => observer.disconnect();
  }, [wrapperRef]);
};

const MapContainerWithUiLayers = ({
  mapElement,
  uiLayers,
}: IMapContainerWithUiLayersProps) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  useAttributionHeightVar(wrapperRef);

  return (
    <div className={mapWrapperCN} ref={wrapperRef}>
      {uiLayers.map((uiLayer, i: number) => (
        <div
          key={i}
          className={classes(
            leafletMapControlsLayerCN,
            style({
              zIndex: i + 1, // 0 is for map
            }),
          )}
        >
          {uiLayer}
        </div>
      ))}
      <div className={leafletMapLayerCN}>{mapElement}</div>
    </div>
  );
};

export default MapContainerWithUiLayers;
